import { Action, createReducer, createFeatureSelector, createSelector, on } from '@ngrx/store';
import { createQuotesSuccess } from '../actions/create-quotes.actions';
import { CartConfig } from '@shared/models/cart-config';
import { setCartConfig } from '../actions/set-cart-config.actions';
import { Group } from '@shared/models/group';
import { loadGroup, loadGroupFailure, loadGroupSuccess } from '@shared/actions/load-group.actions';
import { createCart, createCartSuccess } from '../actions/create-cart.actions';
import { loadCart, loadCartSuccess, loadCartFailure } from '../actions/load-cart.actions';
import { selectCartItem } from '../actions/select-cart-item.actions';
import { removeCartItemSuccess } from '../actions/remove-cart-item.actions';
import { loadQuoteSuccess } from '@features/quotes/shared/actions/load-quote.actions';
import { LoadingState } from '@shared/types/loading-state';
import { updateCustomerSuccess } from '@features/customers/admin/actions/update-customer.actions';
import { CartCustomerValidations } from '@shared/models/cart-customer-validations';
import { Contact } from '@shared/models/contact';
import { ContactRole } from '@shared/types/contact-role';
import { setSelectedOpportunityId } from '../actions/set-selected-opportunity-id.actions';
import { setPreferredPeriod } from '../actions/set-preferred-period.actions';
import { setCartCustomerOrdersExpanded } from '../actions/set-cart-customer-orders-expanded.actions';
import { Period } from '@shared/types/period';
import { Order } from '@shared/models/order';
import {
  loadCartCustomerOrders,
  loadCartCustomerOrdersFailure,
  loadCartCustomerOrdersSuccess,
} from '../actions/load-cart-customer-orders.actions';
import {
  createDiscountRequest,
  createDiscountRequestFailure,
  createDiscountRequestSuccess,
} from '@features/discount-requests/actions/create-discount-request.actions';

export const featureKey = 'cart-config';

export interface State {
  customer: Group | undefined;
  orders: Order[] | undefined;
  ordersLoadingState: LoadingState;
  cartCustomerOrdersExpanded: boolean;
  cartId: string | undefined;
  cartItemId: string | undefined;
  cartConfig: CartConfig | undefined;
  loadingState: LoadingState;
  preferredPeriod: Period | undefined;
}

export const initialState: State = {
  customer: undefined,
  orders: undefined,
  ordersLoadingState: 'not_loading',
  cartCustomerOrdersExpanded: true,
  cartId: undefined,
  cartItemId: undefined,
  cartConfig: undefined,
  loadingState: 'not_loading',
  preferredPeriod: undefined,
};

export const cartConfigReducer = createReducer(
  initialState,
  on(createCart, (state, { cartConfig }) => ({
    ...state,
    cartConfig: cartConfig,
  })),
  on(createCartSuccess, (state, { cart }) => ({
    ...state,
    cartId: cart.id,
    cartConfig: !!state.cartConfig
      ? {
          ...state.cartConfig,
          opportunity_id:
            cart.opportunities && cart.opportunities?.length > 0 && !state.cartConfig.opportunity_id
              ? cart.opportunities[cart.opportunities.length - 1].attributes.id
              : state.cartConfig.opportunity_id,
        }
      : state.cartConfig,
  })),
  on(loadCart, loadCartSuccess, (state) => ({
    ...state,
    preferredPeriod: undefined,
  })),
  on(loadQuoteSuccess, createQuotesSuccess, loadCartFailure, createDiscountRequestSuccess, (state, {}) => ({
    ...state,
    cartId: undefined,
    cartConfig: undefined,
    customer: undefined,
    orders: undefined,
    ordersLoadingState: 'not_loading' as LoadingState,
    cartCustomerOrdersExpanded: true,
    preferredPeriod: undefined,
  })),
  on(setCartConfig, (state, { cartConfig, clearCart }) => ({
    ...state,
    cartConfig: cartConfig,
    cartId: clearCart ? undefined : state.cartId,
    customer: clearCart ? undefined : state.customer,
    orders: clearCart ? undefined : state.orders,
    ordersLoadingState: clearCart ? ('not_loading' as LoadingState) : state.ordersLoadingState,
    cartCustomerOrdersExpanded: clearCart ? true : state.cartCustomerOrdersExpanded,
    preferredPeriod: clearCart ? undefined : state.preferredPeriod,
  })),
  on(loadGroupSuccess, (state, { group }) => {
    return state.cartConfig && group.attributes.group_type === 'customer'
      ? {
          ...state,
          cartConfig: { ...state.cartConfig, group_id: group.attributes.id },
          customer: group,
          orders: undefined,
          ordersLoadingState: 'not_loading' as LoadingState,
          cartCustomerOrdersExpanded: true,
        }
      : state;
  }),
  on(removeCartItemSuccess, (state, { cart }) => {
    return state.cartConfig && state.cartConfig?.quote_type !== cart.quote_type
      ? {
          ...state,
          cartConfig: { ...state.cartConfig, quote_type: cart.quote_type },
        }
      : state;
  }),
  on(selectCartItem, (state, { cartItemId }) => ({
    ...state,
    cartItemId: cartItemId,
  })),
  on(updateCustomerSuccess, (state, { group }) => {
    return state.cartConfig?.group_id === group.attributes.id
      ? {
          ...state,
          customer: group,
        }
      : state;
  }),
  on(loadGroup, createDiscountRequest, (state) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
  })),
  on(loadGroupFailure, createDiscountRequestFailure, (state) => ({
    ...state,
    loadingState: 'not_loading' as LoadingState,
  })),
  on(loadGroupSuccess, createDiscountRequestSuccess, (state) => ({
    ...state,
    loadingState: 'loaded' as LoadingState,
  })),
  on(setSelectedOpportunityId, (state, { opportunityId }) => ({
    ...state,
    cartConfig: state.cartConfig ? { ...state.cartConfig, opportunity_id: opportunityId } : state.cartConfig,
    preferredPeriod: opportunityId ? undefined : state.preferredPeriod,
  })),
  on(setPreferredPeriod, (state, { period }) => ({
    ...state,
    preferredPeriod: period,
  })),
  on(loadCartCustomerOrders, (state) => ({
    ...state,
    ordersLoadingState: 'loading' as LoadingState,
  })),
  on(loadCartCustomerOrdersSuccess, (state, { orders }) => ({
    ...state,
    orders,
    ordersLoadingState: 'loaded' as LoadingState,
  })),
  on(loadCartCustomerOrdersFailure, (state) => ({
    ...state,
    orders: undefined,
    ordersLoadingState: 'not_loading' as LoadingState,
  })),
  on(setCartCustomerOrdersExpanded, (state, { expanded }) => ({
    ...state,
    cartCustomerOrdersExpanded: expanded,
  })),
);

export function reducer(state: State | undefined, action: Action) {
  return cartConfigReducer(state, action);
}

export const cartConfigFeatureSelector = createFeatureSelector<State>(featureKey);

export const selectCartConfigLoadingState = createSelector(cartConfigFeatureSelector, (state) => state.loadingState);

export const selectCartId = createSelector(cartConfigFeatureSelector, (state) => state.cartId);

export const selectCartItemId = createSelector(cartConfigFeatureSelector, (state) => state.cartItemId);

export const selectCartConfig = createSelector(cartConfigFeatureSelector, (state) => state.cartConfig);

export const selectCartCustomer = createSelector(cartConfigFeatureSelector, (state) => state.customer);

export const selectCartCustomerOrders = createSelector(cartConfigFeatureSelector, (state) => state.orders);

export const selectCartCustomerOrdersLoadingState = createSelector(
  cartConfigFeatureSelector,
  (state) => state.ordersLoadingState,
);

export const selectCartCustomerOrdersExpanded = createSelector(
  cartConfigFeatureSelector,
  (state) => state.cartCustomerOrdersExpanded,
);

export const selectCartCustomerContactByRole = (contactRole: ContactRole) =>
  createSelector(selectCartCustomer, (group) =>
    contactRole == undefined
      ? undefined
      : group?.attributes.contacts?.find((contact: Contact) => contact.contact_roles?.includes(contactRole)),
  );

export const selectCartCustomerValidations = createSelector(cartConfigFeatureSelector, (state) => {
  const primaryContact = state.customer?.attributes.contacts?.find((contact: Contact) =>
    contact.contact_roles?.includes('primary'),
  );
  return {
    primaryContactEmailRequired: primaryContact?.email,
    primaryEmailSameAsParentGroupEmail:
      !!state.customer && primaryContact?.email === state.customer?.attributes.parent_group_primary_contact?.email,
    primaryEmailSameAsPartnerContactEmail:
      !!state.customer && primaryContact?.email === state.customer?.attributes.parent_group_partner_contact?.email,
    entityTypeIsNotCompany: state.customer?.attributes.group_entity_type !== 'company',
    crnInvalid:
      state.customer?.attributes.group_entity_type == 'company' &&
      !state.customer?.attributes.crn_exempt &&
      ((!!state.customer && !state.customer?.attributes.company_registration_number) ||
        state.customer?.attributes.company_registration_number.toLowerCase().includes('not available')),
  } as CartCustomerValidations;
});

export const selectSelectedOpportunityId = createSelector(selectCartConfig, (cartConfig) => cartConfig?.opportunity_id);

export const selectPreferredPeriod = createSelector(cartConfigFeatureSelector, (state) => state.preferredPeriod);
